<template>
  <div style="min-height: calc(100vh - 158px)">
    <!-- <div v-if="!submenus.length">No item found</div> -->
    <template v-if="isDashboard(selectedSidebar)">
      <div
        v-if="!isSyncing && shouldShowSync"
        class="d-flex justify-content-end mb-1 align-items-center gap-2"
      >
        <span v-if="lastUpdated" class="text-muted small"
          >Last Updated: {{ lastUpdated }}</span
        >
        <button
          class="btn-sync"
          :disabled="isSyncing"
          @click="syncDashboard"
        >
          <img src="/app-assets/images/icons/dashboard/dashboard_sync.png" alt="Sync Icon" class="sync-icon" />
          <span>Sync</span>
        </button>
      </div>

      <div v-if="isSyncing" class="syncing-overlay">
        <div class="syncing-message">
          <i class="feather icon-loader animation-spin"></i>
          <p>Syncing dashboard data...</p>
          <div class="progress-bar-container mt-3 mb-2">
            <div class="progress-bar-indeterminate"></div>
          </div>
          <small class="text-muted">This may take a few moments</small>
        </div>
      </div>

      <DynamicDashboard
        :submenus="submenus"
        :class="{ 'pointer-events-none': isSyncing }"
        @updated="handleLastUpdated"
      />
    </template>
    <div v-else>
      <template v-for="(item, index) in submenus" :key="index">
        <SubmenuCard v-if="item.status == 'active'" :item="item" />
      </template>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { inject } from "vue";
import SubmenuCard from "@/components/molecule/company/SubmenuCard";
import DynamicDashboard from "@/views/page/company/DynamicDashboard.vue";
import handleSidebar from "@/services/modules/sidebar";
import useDate from "@/services/utils/day";

export default {
  name: "SubmenuPage",

  components: {
    SubmenuCard,
    DynamicDashboard,
  },

  setup() {
    const sidebarMethod = handleSidebar();
    const { formatDateWithTime } = useDate();
    const showSuccess = inject("showSuccess");
    return {
      sidebarMethod,
      formatDateWithTime,
      showSuccess,
    };
  },

  data() {
    return {
      isSyncing: false,
      syncStartTime: null,
      syncDuration: "",
      lastUpdated: null,
    };
  },

  methods: {
    handleLastUpdated(calculatedAt) {
      if (calculatedAt) {
        this.lastUpdated = this.formatDateWithTime(calculatedAt);
      }
    },
    isDashboard(selectedSidebar) {
      return selectedSidebar?.is_dashboard;
    },

    async syncDashboard() {
      this.isSyncing = true;
      this.syncStartTime = new Date();

      try {
        await this.sidebarMethod.refreshDashboard(
          `?company_id=${this.$route.params.companyId}&module_id=${this.$route.params.moduleId}`,
        );

        // Calculate duration
        const duration = Math.round((new Date() - this.syncStartTime) / 1000);
        this.syncDuration = `${duration} seconds`;

        // Trigger reload event - this will fetch new data and update cache
        window.dispatchEvent(new CustomEvent("reload-dashboard-summaries"));
        this.showSuccess("Dashboard synced successfully!");
      } catch (error) {
        console.error("Sync failed:", error);
      } finally {
        this.isSyncing = false;
      }
    },
  },

  computed: {
    ...mapGetters({
      sidebar: "company/getSidebar",
    }),

    selectedSidebar() {
      return this.sidebar.find((i) => i.id == this.$route.params.menuId);
    },

    submenus() {
      return this.selectedSidebar?.submenus
        ? this.selectedSidebar.submenus
        : [];
    },

    shouldShowSync() {
      return this.submenus.some(submenu =>
        submenu.pages.some(page =>
          page.status === 'active' && page.card_type !== 'input' && page.card_type !== 'list'
        )
      );
    },
  },
};
</script>

<style scoped>
.btn-sync {
  display: flex;
  align-items: center;
  background-color: #e3f2f1;
  color: #0d616a;
  border: none;
  border-radius: 100px;
  padding: 8px 25px;
  font-weight: 700;
  font-size: 13px;
  transition: all 0.3s ease;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 1px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.btn-sync i {
  font-size: 16px;
}

.sync-icon {
  width: 15px;
  height: 15px;
  margin-right: 10px;
}

.btn-sync:hover:not(:disabled) {
  background-color: #d1e8e7;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.btn-sync:active:not(:disabled) {
  transform: translateY(0);
}

.btn-sync:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.syncing-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
}

.syncing-message {
  background: white;
  padding: 30px 50px;
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
}

.syncing-message i {
  font-size: 40px;
  color: #007bff;
  margin-bottom: 15px;
  display: block;
}

.syncing-message p {
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 8px 0;
}

.syncing-message small {
  color: #6c757d;
}

.animation-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.pointer-events-none {
  pointer-events: none;
}

.progress-bar-container {
  width: 100%;
  height: 6px;
  background-color: #f1f1f1;
  border-radius: 10px;
  overflow: hidden;
  position: relative;
}

.progress-bar-indeterminate {
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    #28c76f,
    #20c997,
    transparent
  );
  border-radius: 10px;
  width: 50%;
  position: absolute;
  animation: indeterminate 1.5s infinite ease-in-out;
}

@keyframes indeterminate {
  0% {
    left: -50%;
  }
  100% {
    left: 100%;
  }
}
</style>
